import { Command, useCommandState } from "cmdk";
import { memo, useCallback, useEffect, useState, type FC } from "react";
import { atom, useAtom, useAtomValue } from "jotai";
import { useSearchRepositoriesQuery } from "@/queries/runner-queries";
import { Skeleton } from "@/components/Skeleton";
import { SearchMode, type SearchRepositoriesResponse_Repository } from "gitpod-next-api/gitpod/v1/runner_pb";
import type { PlainMessage } from "@bufbuild/protobuf";
import { useDebounce } from "use-debounce";
import { lastSelectedScmAuthHostAtom, onboardingRunnerAtom } from "@/routes/ona-onboarding/onboarding-state";
import IconMagnifyingGlass from "central-icons/IconMagnifyingGlass";
import { formatError } from "@/utils/errors";
import { useSegmentTrack } from "@/hooks/use-segment";

const searchQueryAtom = atom("");

export const RepositorySelectorMenu: FC<{
    onSelect: (repoUrl: string, repoName: string, selectionIndex: number, totalAvailable: number) => void;
}> = ({ onSelect }) => {
    return (
        <div className="rounded-lg border border-border-base bg-surface-popover shadow-md">
            <Command>
                <div className="flex items-center border-b border-b-border-base">
                    <IconMagnifyingGlass className="ml-3 size-4" />
                    <RepositorySearchInput />
                </div>
                <div className="max-h-32 overflow-y-auto px-1">
                    <Command.List label="Repositories">
                        <RepositoryCommandGroup onSelectRepo={onSelect} />
                    </Command.List>
                </div>
                <div className="text-foreground-muted border-t border-t-border-base px-3 py-2 text-sm">
                    Can&apos;t find the repository you&apos;re looking for?{" "}
                    <a
                        href="https://ona.com/docs/ona/source-control/github#granting-access-to-additional-github-organizations"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-content-brand hover:underline"
                    >
                        Grant organization access
                    </a>
                </div>
            </Command>
        </div>
    );
};

const RepositorySearchInput: FC = () => {
    const [value, setValue] = useAtom(searchQueryAtom);
    return (
        <Command.Input
            autoFocus
            value={value}
            onValueChange={setValue}
            className="w-full flex-1 bg-transparent py-3 pl-2 pr-3 text-base focus:outline-none focus:ring-0"
            placeholder="Type to search for private repositories"
        />
    );
};

const RepositoryCommandGroup: FC<{
    onSelectRepo: (repoUrl: string, repoName: string, selectionIndex: number, totalAvailable: number) => void;
}> = ({ onSelectRepo }) => {
    const onboardingRunnerQuery = useAtomValue(onboardingRunnerAtom);
    const [shouldShowUserRepos, setShouldShowUserRepos] = useState(false);
    const queryKeyword = useAtomValue(searchQueryAtom);
    const [debouncedQueryKeyword, { isPending: isQueryDebouncePending }] = useDebounce(queryKeyword, 200);
    const hasNoLocalResults = useCommandState((state) => state.filtered.count === 0);
    const segmentTrack = useSegmentTrack();
    const lastSelectedScmAuthHost = useAtomValue(lastSelectedScmAuthHostAtom);
    const {
        data: repositories,
        status,
        error: searchQueryError,
    } = useSearchRepositoriesQuery({
        scmHost: lastSelectedScmAuthHost ?? "",
        runnerId: onboardingRunnerQuery.type === "success" ? onboardingRunnerQuery.data.runnerId : "",
        query: shouldShowUserRepos ? "" : debouncedQueryKeyword,
        limit: 25,
        searchMode: SearchMode.KEYWORD,
    });

    // useCallback because it is passed to a memoed component
    const _onSelectRepo = useCallback(
        (repoUrl: string, repoName: string, selectionIndex: number) => {
            if (onboardingRunnerQuery.type !== "success") {
                return;
            }

            onSelectRepo(repoUrl, repoName, selectionIndex, repositories?.length ?? 0);
        },
        [onboardingRunnerQuery, onSelectRepo, repositories],
    );

    // this useEffect determines whether to filter user's repositories or to perform a network query:
    // local search is always prioritized over network search, until there is no more local results to show
    // if there are no local results and the query is not empty, we should perform a network query
    // if the query is reset back to empty, we should show the user's repositories
    useEffect(() => {
        if (hasNoLocalResults && debouncedQueryKeyword) {
            setShouldShowUserRepos(false);
        } else if (!shouldShowUserRepos && !debouncedQueryKeyword) {
            setShouldShowUserRepos(true);
        }
    }, [debouncedQueryKeyword, hasNoLocalResults, shouldShowUserRepos]);

    // Track when repositories are loaded
    useEffect(() => {
        if (repositories && repositories.length > 0) {
            segmentTrack("onboarding_repositories_loaded", {
                location: "onboarding_select_repository",
                repository_count: repositories.length,
                repository_names: repositories.map((repo) => repo.name),
                has_search_query: Boolean(debouncedQueryKeyword),
            });
        }
    }, [repositories, debouncedQueryKeyword, segmentTrack]);

    if (isQueryDebouncePending() && hasNoLocalResults) {
        return <LoadingSkeleton />;
    }

    switch (status) {
        case "pending":
            return (
                <Command.Loading>
                    <LoadingSkeleton />
                </Command.Loading>
            );
        case "success":
            return (
                <>
                    <Command.Empty className="px-3 py-2 text-base opacity-50">No repositories found</Command.Empty>
                    {repositories.length > 0 ? (
                        <Command.Group className="py-1">
                            <RepositoryList repositories={repositories} onSelect={_onSelectRepo} />
                        </Command.Group>
                    ) : null}
                </>
            );
        case "error":
            return (
                <p className="my-1 w-full text-wrap rounded bg-red-50 px-3 py-1 text-base text-red-500 dark:bg-red-900 dark:text-red-100">
                    {formatError(searchQueryError)}
                </p>
            );
    }
};

const LoadingSkeleton: FC = memo(() => {
    return (
        <div className="space-y-2 py-1.5">
            {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-6 w-full bg-surface-accent dark:bg-surface-accent" />
            ))}
        </div>
    );
});
LoadingSkeleton.displayName = "LoadingSkeleton";

const RepositoryList: FC<{
    repositories: PlainMessage<SearchRepositoriesResponse_Repository>[];
    onSelect: (url: string, name: string, selectionIndex: number) => void;
}> = memo(({ repositories, onSelect }) => {
    // useCallback because it is passed to a memoed component
    const handleSelect = useCallback(
        (url: string, name: string, selectionIndex: number) => {
            onSelect(url, name, selectionIndex);
        },
        [onSelect],
    );
    return (
        <>
            {repositories.map(({ name, url }, index) => (
                <RepositoryListItem
                    key={url}
                    name={name}
                    url={url}
                    index={index}
                    onSelect={handleSelect}
                    disabled={false}
                />
            ))}
        </>
    );
});
RepositoryList.displayName = "RepositoryList";

const RepositoryListItem: FC<{
    name: string;
    url: string;
    index: number;
    onSelect: (url: string, name: string, index: number) => void;
    disabled: boolean;
}> = memo(({ name, url, index, onSelect, disabled }) => {
    const _onSelect = useCallback(() => {
        onSelect(url, name, index);
    }, [onSelect, url, name, index]);
    return (
        <Command.Item
            className="flex w-full cursor-pointer items-center justify-between rounded px-2 py-1.5 text-start text-base hover:bg-surface-hover hover:bg-surface-primary data-[selected=true]:bg-surface-hover data-[selected=true]:bg-surface-primary data-[disabled=true]:opacity-50"
            onSelect={_onSelect}
            disabled={disabled}
            value={`${name} ${url}`}
        >
            {name}
        </Command.Item>
    );
});
RepositoryListItem.displayName = "RepositoryListItem";
